// frontend/src/utils/languageDetection.js
/**
 * IP-based language detection utility
 * Detects user's country and maps to appropriate OFFICIAL language
 * PRIORITY: Official/Administrative languages first (EN, FR, PT, AR, ES)
 */

const COUNTRY_TO_LANGUAGE_MAP = {
    // West Africa - OFFICIAL LANGUAGES ONLY
    GH: 'en',  // Ghana → English (Official)
    NG: 'en',  // Nigeria → English (Official)
    SN: 'fr',  // Senegal → French
    CI: 'fr',  // Côte d'Ivoire → French
    ML: 'fr',  // Mali → French
    BF: 'fr',  // Burkina Faso → French
    BJ: 'fr',  // Benin → French
    TG: 'fr',  // Togo → French
    NE: 'fr',  // Niger → French
    GW: 'pt',  // Guinea-Bissau → Portuguese
    GM: 'en',  // Gambia → English
    SL: 'en',  // Sierra Leone → English
    LR: 'en',  // Liberia → English

    // East Africa - OFFICIAL LANGUAGES
    KE: 'en',  // Kenya → English (Official)
    TZ: 'en',  // Tanzania → English (Official) - Not Swahili by default
    UG: 'en',  // Uganda → English (Official)
    RW: 'en',  // Rwanda → English (changed from FR as EN is now co-official)
    BI: 'fr',  // Burundi → French
    ET: 'en',  // Ethiopia → English (International/working language)
    SO: 'ar',  // Somalia → Arabic (Official)

    // North Africa
    EG: 'ar',  // Egypt → Arabic
    SD: 'ar',  // Sudan → Arabic
    LY: 'ar',  // Libya → Arabic
    TN: 'ar',  // Tunisia → Arabic
    DZ: 'ar',  // Algeria → Arabic
    MA: 'ar',  // Morocco → Arabic
    MR: 'ar',  // Mauritania → Arabic

    // Southern Africa
    ZA: 'en',  // South Africa → English
    ZW: 'en',  // Zimbabwe → English
    ZM: 'en',  // Zambia → English
    BW: 'en',  // Botswana → English
    NA: 'en',  // Namibia → English
    LS: 'en',  // Lesotho → English
    SZ: 'en',  // Eswatini → English
    MW: 'en',  // Malawi → English

    // Central Africa
    CM: 'fr',  // Cameroon → French
    GA: 'fr',  // Gabon → French
    CG: 'fr',  // Congo → French
    CD: 'fr',  // DR Congo → French
    CF: 'fr',  // Central African Republic → French
    TD: 'fr',  // Chad → French
    GQ: 'es',  // Equatorial Guinea → Spanish

    // Lusophone Africa
    AO: 'pt',  // Angola → Portuguese
    MZ: 'pt',  // Mozambique → Portuguese
    CV: 'pt',  // Cape Verde → Portuguese
    ST: 'pt',  // São Tomé and Príncipe → Portuguese

    // Other regions
    US: 'en',  // United States
    GB: 'en',  // United Kingdom
    CA: 'en',  // Canada
    FR: 'fr',  // France
    PT: 'pt',  // Portugal
    ES: 'es',  // Spain
    BR: 'pt',  // Brazil
};

/**
 * Detect language from IP address
 * @returns {Promise<string>} Language code (en, fr, ar, pt, sw, yo, ig, es, tw, ha, zu, am)
 */
export async function detectLanguageFromIP() {
    // ✅ CRITICAL: Add a total timeout for the entire detection process
    // This prevents the app from hanging on a white screen if APIs are blocked (e.g. by Brave Shields)
    const TIMEOUT_MS = 5000;

    const detectionPromise = (async () => {
        try {
            // Check if user has already selected a language
            const savedLanguage = localStorage.getItem('gigship_language') || localStorage.getItem('preferredLanguage');
            if (savedLanguage) {
                console.log('🌍 Using saved language preference:', savedLanguage);
                return savedLanguage;
            }


            // Try ipapi.co first (30k requests/month free)
            try {
                const response = await fetch('https://ipapi.co/json/', {
                    signal: AbortSignal.timeout(3000)
                });

                if (response.ok) {
                    const data = await response.json();
                    const countryCode = data.country_code;
                    const detectedLanguage = mapCountryToLanguage(countryCode);

                    console.log('🌍 IP Detection:', {
                        country: data.country_name,
                        code: countryCode,
                        language: detectedLanguage
                    });

                    return detectedLanguage;
                }
            } catch (error) {
                console.warn('ipapi.co failed, trying fallback...');
            }

            // Fallback to ip-api.com (45 requests/minute free)
            try {
                const fallbackResponse = await fetch('http://ip-api.com/json/', {
                    signal: AbortSignal.timeout(3000)
                });

                if (fallbackResponse.ok) {
                    const data = await fallbackResponse.json();
                    if (data.status === 'success') {
                        const countryCode = data.countryCode;
                        const detectedLanguage = mapCountryToLanguage(countryCode);

                        console.log('🌍 IP Detection (fallback):', {
                            country: data.country,
                            code: countryCode,
                            language: detectedLanguage
                        });

                        return detectedLanguage;
                    }
                }
            } catch (error) {
                console.warn('ip-api.com failed, using browser fallback...');
            }

            // If all fails, use browser language
            return detectFromBrowser();

        } catch (error) {
            console.error('❌ IP detection failed:', error);
            return detectFromBrowser();
        }
    })();

    // Race the detection against a timeout
    return Promise.race([
        detectionPromise,
        new Promise((resolve) => {
            setTimeout(() => {
                console.warn('⚠️ IP detection timed out after 5s, falling back to browser language');
                resolve(detectFromBrowser());
            }, TIMEOUT_MS);
        })
    ]);
}

/**
 * Map country code to OFFICIAL language
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} Language code
 */
function mapCountryToLanguage(countryCode) {
    const language = COUNTRY_TO_LANGUAGE_MAP[countryCode];

    if (language) {
        return language;
    }

    // Default to English for unmapped countries
    console.log('⚠️ Country not mapped:', countryCode, '- defaulting to English');
    return 'en';
}

/**
 * Detect language from browser settings
 * @returns {string} Language code
 */
function detectFromBrowser() {
    const browserLang = navigator.language || navigator.userLanguage;
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Map browser language to supported languages
    const supported = ['en', 'fr', 'ar', 'pt', 'sw', 'yo', 'ig', 'es', 'tw', 'ha', 'zu', 'am'];

    if (supported.includes(langCode)) {
        console.log('🌍 Using browser language:', langCode);
        return langCode;
    }

    console.log('🌍 Browser language not supported, defaulting to English');
    return 'en';
}

/**
 * Get language display name
 * @param {string} code - Language code
 * @returns {object} Display info {name, nativeName, flag}
 */
export function getLanguageInfo(code) {
    const languages = {
        en: { name: 'English', nativeName: 'English', flag: '🇬🇧', code: 'EN' },
        fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷', code: 'FR' },
        ar: { name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', code: 'AR', rtl: true },
        pt: { name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', code: 'PT' },
        sw: { name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪', code: 'SW' },
        yo: { name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', code: 'YO' },
        ig: { name: 'Igbo', nativeName: 'Igbo', flag: '🇳🇬', code: 'IG' },
        es: { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', code: 'ES' },
        tw: { name: 'Twi', nativeName: 'Twi', flag: '🇬🇭', code: 'TW' },
        ha: { name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬', code: 'HA' },
        zu: { name: 'Zulu', nativeName: 'Zulu', flag: '🇿🇦', code: 'ZU' },
        am: { name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', code: 'AM' }
    };

    return languages[code] || languages.en;
}

/**
 * Get all supported languages - OFFICIAL LANGUAGES FIRST
 * @returns {Array} Array of language objects
 */
export function getSupportedLanguages() {
    return [
        // Official/Administrative Languages First
        { code: 'en', ...getLanguageInfo('en') },
        { code: 'fr', ...getLanguageInfo('fr') },
        { code: 'pt', ...getLanguageInfo('pt') },
        { code: 'ar', ...getLanguageInfo('ar') },
        { code: 'es', ...getLanguageInfo('es') },

        // Local/Regional Languages Second
        { code: 'sw', ...getLanguageInfo('sw') },
        { code: 'am', ...getLanguageInfo('am') },
        { code: 'ha', ...getLanguageInfo('ha') },
        { code: 'yo', ...getLanguageInfo('yo') },
        { code: 'ig', ...getLanguageInfo('ig') },
        { code: 'tw', ...getLanguageInfo('tw') },
        { code: 'zu', ...getLanguageInfo('zu') }
    ];
}

/**
 * Save language preference
 * @param {string} code - Language code
 */
export function saveLanguagePreference(code) {
    localStorage.setItem('gigship_language', code);
    console.log('💾 Saved language preference:', code);
}


/**
 * Check if language needs RTL
 * @param {string} code - Language code
 * @returns {boolean}
 */
export function isRTL(code) {
    return code === 'ar';
}

export default {
    detectLanguageFromIP,
    getLanguageInfo,
    getSupportedLanguages,
    saveLanguagePreference,
    isRTL
};